'use client';

import { useEffect, useRef, useState } from 'react';

import { useInView, useReducedMotion } from 'motion/react';

/**
 * ChatPhone — a realistic looping WhatsApp phone mock for the web.
 *
 * Web port of the Selenos ad's Remotion `WhatsAppQualify` composition: a
 * portrait iPhone (bezel, dynamic island, iOS status bar, WhatsApp green
 * header with avatar + live status, doodle wallpaper, tailed bubbles, typing
 * dots, blue read-ticks, input bar, home indicator) running a scripted
 * lead-qualification chat that ends on a teal "Qualified" chip.
 *
 * The conversation is an EXAMPLE BUSINESS (a Selenos customer) qualifying its
 * own lead — not Selenos qualifying anyone. Copy is passed in, localized per
 * page locale, so the agent visibly speaks the lead's language.
 *
 * Obeys the selenos-marketing-page motion rules: transform/opacity only, plays
 * only in view, pauses offscreen, graceful loop, and freezes on the full
 * end-state under prefers-reduced-motion.
 */

// Authentic WhatsApp palette (mirrors the Remotion comp).
const WA = {
  header: '#128C7E',
  chat: '#ECE5DD',
  bubbleIn: '#FFFFFF',
  bubbleOut: '#D9FDD3',
  timestamp: '#667781',
  tickBlue: '#34B7F1',
  input: '#F6F6F6',
  send: '#25D366',
} as const;

const WALLPAPER =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cg fill='none' stroke='%23888' stroke-width='0.8' opacity='0.16'%3E%3Ccircle cx='20' cy='20' r='6'/%3E%3Ccircle cx='80' cy='50' r='4'/%3E%3Cpath d='M40 70 Q55 55 70 70'/%3E%3Crect x='90' y='10' width='14' height='14' rx='3'/%3E%3Ccircle cx='55' cy='100' r='5'/%3E%3Cpath d='M100 80 Q110 90 100 100'/%3E%3C/g%3E%3C/svg%3E\")";

export interface ChatPhoneCopy {
  business: string; // example business name (e.g. "Helios Solar")
  status: string; // header idle status ("AI agent · replies instantly")
  typing: string; // header status while composing ("typing…")
  /** Alternating script: [in, out, in, out, in]. Last must be inbound. */
  messages: { dir: 'in' | 'out'; text: string }[];
  qualified: string; // chip label ("Qualified")
  example: string; // honesty tag ("example")
  inputPlaceholder: string; // input bar ghost text
}

type Phase = 'play' | 'hold' | 'reset';

const TYPING_MS = 850; // typing dots before an inbound reply
const OUT_GAP_MS = 700; // pause after a lead (outbound) reply
const IN_GAP_MS = 950; // pause after an agent (inbound) reply
const HOLD_MS = 2800; // hold the Qualified payoff before looping
const RESET_MS = 480; // graceful fade-out before restart

/** Scripted, in-view, reduced-motion-safe chat timeline. */
function useScriptedChat(count: number, startDelayMs: number) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.4 });
  const reduce = useReducedMotion();

  const [visible, setVisible] = useState(0); // messages shown
  const [typing, setTyping] = useState(false); // typing dots showing
  const [phase, setPhase] = useState<Phase>('play');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    if (reduce) {
      setVisible(count);
      setTyping(false);
      setPhase('hold');
      return clearAll;
    }
    if (!inView) return clearAll;

    let dead = false;
    const at = (ms: number, fn: () => void) => {
      const id = setTimeout(() => {
        if (!dead) fn();
      }, ms);
      timers.current.push(id);
    };

    const run = () => {
      clearAll();
      setVisible(0);
      setTyping(false);
      setPhase('play');

      let t = startDelayMs + 250;
      for (let i = 0; i < count; i++) {
        // index 0 is the agent opener — no typing before it.
        const inbound = i % 2 === 0;
        if (inbound && i > 0) {
          at(t, () => setTyping(true));
          t += TYPING_MS;
          at(t, () => setTyping(false));
        }
        const reveal = i;
        at(t, () => setVisible(reveal + 1));
        t += inbound ? IN_GAP_MS : OUT_GAP_MS;
      }
      // Qualified chip rides in just after the final inbound bubble.
      at(t, () => setPhase('hold'));
      t += HOLD_MS;
      at(t, () => setPhase('reset'));
      t += RESET_MS;
      at(t, run); // loop
    };

    run();
    return () => {
      dead = true;
      clearAll();
    };
  }, [inView, reduce, count, startDelayMs]);

  return { ref, visible, typing, phase, reduce };
}

function Tail({ side }: { side: 'in' | 'out' }) {
  const bg = side === 'in' ? WA.bubbleIn : WA.bubbleOut;
  return (
    <svg
      width="8"
      height="10"
      viewBox="0 0 8 10"
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        [side === 'in' ? 'left' : 'right']: 0,
      }}
    >
      <path
        d={side === 'in' ? 'M8 0 L0 0 L8 10' : 'M0 0 L8 0 L0 10'}
        fill={bg}
      />
    </svg>
  );
}

function Bubble({
  dir,
  text,
  time,
  ticks,
}: {
  dir: 'in' | 'out';
  text: string;
  time: string;
  ticks: boolean;
}) {
  const isIn = dir === 'in';
  return (
    <div
      className="sel-chat-rise"
      style={{
        display: 'flex',
        justifyContent: isIn ? 'flex-start' : 'flex-end',
        marginBottom: 6,
        position: 'relative',
        paddingLeft: isIn ? 7 : 0,
        paddingRight: isIn ? 0 : 7,
      }}
    >
      {isIn && <Tail side="in" />}
      <div
        style={{
          maxWidth: '82%',
          padding: '5px 8px 4px',
          borderRadius: 8,
          borderTopLeftRadius: isIn ? 1 : 8,
          borderTopRightRadius: isIn ? 8 : 1,
          background: isIn ? WA.bubbleIn : WA.bubbleOut,
          boxShadow: '0 1px 1px rgba(0,0,0,0.12)',
        }}
      >
        <span
          style={{
            color: '#111B1A',
            fontSize: 11.5,
            lineHeight: 1.34,
            display: 'block',
          }}
        >
          {text}
        </span>
        <div
          aria-hidden="true"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 3,
            marginTop: 1,
          }}
        >
          <span
            className="sel-mono"
            style={{ color: WA.timestamp, fontSize: 8 }}
          >
            {time}
          </span>
          {!isIn && (
            <span
              style={{
                color: ticks ? WA.tickBlue : WA.timestamp,
                fontSize: 9,
                letterSpacing: -1.5,
                lineHeight: 1,
                transition: 'color 0.3s',
              }}
            >
              ✓✓
            </span>
          )}
        </div>
      </div>
      {!isIn && <Tail side="out" />}
    </div>
  );
}

function TypingDots() {
  return (
    <div
      className="sel-chat-rise"
      style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 6 }}
    >
      <div
        style={{
          display: 'flex',
          gap: 4,
          alignItems: 'center',
          padding: '8px 11px',
          borderRadius: 8,
          borderTopLeftRadius: 1,
          background: WA.bubbleIn,
          boxShadow: '0 1px 1px rgba(0,0,0,0.12)',
        }}
      >
        {[0, 200, 400].map((d) => (
          <span
            key={d}
            className="sel-typing-dot"
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: WA.timestamp,
              animationDelay: `${d}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/** Generic WhatsApp Business storefront avatar (unbranded). */
function StoreAvatar() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      <circle cx="15" cy="15" r="15" fill="#DFE5E7" />
      <g transform="translate(7 7)" fill="#FFFFFF">
        <path d="M0 5 L1.4 1 H14.6 L16 5 Q16 7 14 7 Q12.4 7 12.4 5 Q12.4 7 10.4 7 Q8.4 7 8.4 5 Q8.4 7 6.4 7 Q4.4 7 4.4 5 Q4.4 7 2.4 7 Q0 7 0 5 Z" />
        <rect x="1.4" y="7.6" width="13.2" height="6.4" rx="0.6" />
      </g>
      <rect x="18" y="17" width="3" height="4.5" rx="0.4" fill="#DFE5E7" />
    </svg>
  );
}

export function ChatPhone({
  copy,
  className,
}: {
  copy: ChatPhoneCopy;
  className?: string;
}) {
  const count = copy.messages.length;
  const { ref, visible, typing, phase } = useScriptedChat(count, 0);

  const times = ['09:41', '09:41', '09:42', '09:42', '09:43', '09:43', '09:44'];
  // No `reduce` here: useReducedMotion is null on SSR but real on the first
  // client render, so branching markup on it hydration-mismatches (React
  // #418). The effect in useScriptedChat sets phase='hold' for reduced
  // motion right after mount, which shows the chip via the phase check.
  const showQualified = phase === 'hold' || phase === 'reset';
  // graceful loop: fade the whole screen content out on reset
  const screenOpacity = phase === 'reset' ? 0 : 1;

  return (
    <div ref={ref} className={className} style={{ perspective: 1000 }}>
      <div
        style={{
          width: '100%',
          maxWidth: 300,
          margin: '0 auto',
          aspectRatio: '300 / 610',
          background: '#1A1A1C',
          borderRadius: 44,
          padding: 6,
          boxShadow:
            '0 30px 80px -30px rgba(20,32,31,0.45), 0 8px 24px -12px rgba(20,32,31,0.3), inset 0 0 0 1px rgba(255,255,255,0.06)',
        }}
      >
        {/* SCREEN */}
        <div
          style={{
            position: 'relative',
            height: '100%',
            borderRadius: 38,
            overflow: 'hidden',
            background: WA.chat,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Dynamic island */}
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 78,
              height: 22,
              background: '#000',
              borderRadius: 11,
              zIndex: 20,
            }}
          />

          {/* iOS status bar — decorative device chrome, mimics WhatsApp's
              real palette so it can't meet 4.5:1; excluded from the a11y
              tree instead. */}
          <div
            aria-hidden="true"
            style={{
              height: 34,
              background: WA.header,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              flexShrink: 0,
            }}
          >
            <span
              className="sel-mono"
              style={{ color: '#fff', fontSize: 11, fontWeight: 600 }}
            >
              9:41
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <svg width="14" height="9" viewBox="0 0 18 12" aria-hidden="true">
                <rect x="0" y="6" width="3" height="6" rx="0.5" fill="#fff" />
                <rect x="5" y="4" width="3" height="8" rx="0.5" fill="#fff" />
                <rect x="10" y="2" width="3" height="10" rx="0.5" fill="#fff" />
                <rect x="15" y="0" width="3" height="12" rx="0.5" fill="#fff" />
              </svg>
              <svg
                width="22"
                height="11"
                viewBox="0 0 30 15"
                aria-hidden="true"
              >
                <rect
                  x="0.5"
                  y="1.5"
                  width="24"
                  height="12"
                  rx="3"
                  stroke="#fff"
                  strokeWidth="1.2"
                  fill="none"
                />
                <rect
                  x="25"
                  y="5"
                  width="3"
                  height="5"
                  rx="1.5"
                  fill="#fff"
                  opacity="0.6"
                />
                <rect
                  x="2"
                  y="3.5"
                  width="17"
                  height="8"
                  rx="1.5"
                  fill="#fff"
                />
              </svg>
            </div>
          </div>

          {/* WhatsApp header — decorative chrome (see status bar note) */}
          <div
            aria-hidden="true"
            style={{
              height: 44,
              background: WA.header,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 10px',
              flexShrink: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
            }}
          >
            <span
              style={{
                color: '#fff',
                fontSize: 20,
                opacity: 0.9,
                lineHeight: 1,
              }}
            >
              ‹
            </span>
            <StoreAvatar />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  color: '#fff',
                  fontSize: 12.5,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {copy.business}
              </span>
              <span
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: 9.5,
                  lineHeight: 1.2,
                }}
              >
                {typing ? copy.typing : copy.status}
              </span>
            </div>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"
                fill="#fff"
              />
            </svg>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.21c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                fill="#fff"
              />
            </svg>
          </div>

          {/* Chat area */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              backgroundColor: WA.chat,
              backgroundImage: WALLPAPER,
              backgroundSize: '120px 120px',
              position: 'relative',
              overflow: 'hidden',
              opacity: screenOpacity,
              transition: `opacity ${RESET_MS}ms ease`,
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                padding: '10px 9px 0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              {copy.messages.slice(0, visible).map((m, i) => (
                <Bubble
                  key={i}
                  dir={m.dir}
                  text={m.text}
                  time={times[i] ?? '09:44'}
                  ticks={m.dir === 'out'}
                />
              ))}
              {typing && <TypingDots />}
              {showQualified && (
                <div
                  className="sel-chat-pop"
                  style={{ display: 'flex', marginTop: 2, marginBottom: 6 }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      background: 'var(--sel-accent)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: '3px 9px',
                      boxShadow: '0 4px 14px rgba(14,110,110,0.3)',
                    }}
                  >
                    ✓ {copy.qualified}
                  </span>
                  <span
                    className="sel-mono"
                    style={{
                      alignSelf: 'center',
                      marginLeft: 8,
                      fontSize: 8,
                      letterSpacing: '0.1em',
                      color: 'rgba(107,122,120,0.7)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {copy.example}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Input bar — decorative chrome (see status bar note) */}
          <div
            aria-hidden="true"
            style={{
              height: 40,
              background: WA.input,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '0 8px',
              flexShrink: 0,
              borderTop: '1px solid rgba(0,0,0,0.07)',
            }}
          >
            <span style={{ color: WA.timestamp, fontSize: 18, lineHeight: 1 }}>
              +
            </span>
            <div
              style={{
                flex: 1,
                height: 24,
                background: '#fff',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                fontSize: 10.5,
                color: 'rgba(107,122,120,0.7)',
                border: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              {copy.inputPlaceholder}
            </div>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: WA.send,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <rect x="7" y="1" width="6" height="11" rx="3" fill="#fff" />
                <path
                  d="M4 10 Q4 16 10 16 Q16 16 16 10"
                  stroke="#fff"
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinecap="round"
                />
                <line
                  x1="10"
                  y1="16"
                  x2="10"
                  y2="19"
                  stroke="#fff"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          {/* Home indicator */}
          <div
            style={{
              height: 18,
              background: WA.input,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 96,
                height: 4,
                borderRadius: 2,
                background: 'rgba(0,0,0,0.25)',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
